/**
 * Base API Service
 * Provides common middleware patterns for all API services
 * - Authentication headers
 * - Timeout handling
 * - Retry logic with exponential backoff
 * - Rate limit handling
 * - Error handling
 */

import { getAuthToken } from '../lib/authTokenStore'
import { clientLogger } from '../utils/clientLogger'

export interface ApiRequestConfig {
  timeout?: number
  retries?: number
  retryDelay?: number
  retryOn?: number[] // HTTP status codes to retry on
  skipAuth?: boolean
  additionalHeaders?: Record<string, string>
}

export interface ApiServiceConfig {
  baseUrl: string
  defaultTimeout?: number
  defaultRetries?: number
  enableRetry?: boolean
}

export class BaseApiService {
  protected baseUrl: string
  protected defaultTimeout: number
  protected defaultRetries: number
  protected enableRetry: boolean

  constructor(config: ApiServiceConfig) {
    this.baseUrl = config.baseUrl
    this.defaultTimeout = config.defaultTimeout || 15000 // 15s default
    this.defaultRetries = config.defaultRetries || 3
    this.enableRetry = config.enableRetry !== false // Enabled by default
  }

  /**
   * Get authentication headers.
   * Reads the current token from the centralized auth token store, which
   * AuthContext keeps updated via onAuthStateChange. Synchronous — no I/O,
   * no race conditions with Supabase token refresh.
   */
  protected async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    const token = getAuthToken()
    clientLogger.debug({ tokenPresent: token ? 'PRESENT' : 'NULL' }, 'Token from store')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  /**
   * Fetch with timeout to prevent indefinite hangs
   * Can be overridden for custom timeout behavior
   */
  protected async fetchWithTimeout(
    url: string,
    options: RequestInit = {},
    timeout: number = this.defaultTimeout
  ): Promise<Response> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      })
      clearTimeout(timeoutId)
      return response
    } catch (error: any) {
      clearTimeout(timeoutId)
      if (error.name === 'AbortError') {
        throw new Error(`Request timeout after ${timeout}ms: ${url}`)
      }
      throw error
    }
  }

  /**
   * Make API request with all middleware applied
   * This is the main method services should use
   */
  protected async request<T>(
    endpoint: string,
    options: RequestInit = {},
    config: ApiRequestConfig = {}
  ): Promise<T> {
    const {
      timeout = this.defaultTimeout,
      retries = this.defaultRetries,
      retryDelay = 1000,
      retryOn = [429, 500, 502, 503, 504],
      skipAuth = false,
      additionalHeaders = {},
    } = config

    // Build headers
    let headers: Record<string, string> = {}

    if (!skipAuth) {
      headers = await this.getAuthHeaders()
    } else {
      headers = { 'Content-Type': 'application/json' }
    }

    // Merge additional headers
    headers = { ...headers, ...additionalHeaders }

    // Build full URL
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`

    // Attempt request with retries
    let lastError: Error | null = null
    const maxAttempts = this.enableRetry ? retries + 1 : 1

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await this.fetchWithTimeout(
          url,
          { ...options, headers },
          timeout
        )

        // Handle successful responses
        if (response.ok) {
          // Check if response has content
          const contentType = response.headers.get('content-type')
          if (contentType && contentType.includes('application/json')) {
            return await response.json()
          }
          return {} as T // Empty response
        }

        // Handle error responses
        await this.handleErrorResponse(response, attempt, maxAttempts, retryOn, retryDelay)

      } catch (error: any) {
        lastError = error

        // Check if we should retry
        const isRetryable = this.isRetryableError(error)
        const shouldRetry = this.enableRetry && isRetryable && attempt < maxAttempts - 1

        if (shouldRetry) {
          const delay = retryDelay * Math.pow(2, attempt) // Exponential backoff
          clientLogger.warn({ delay, attempt: attempt + 1, maxAttempts }, `Retrying request in ${delay}ms`)
          await this.sleep(delay)
          continue
        }

        // No more retries, throw error
        throw error
      }
    }

    throw lastError || new Error('Request failed after all retries')
  }

  /**
   * Handle error responses
   * Can be overridden for custom error handling
   */
  protected async handleErrorResponse(
    response: Response,
    attempt: number,
    maxAttempts: number,
    retryOn: number[],
    retryDelay: number
  ): Promise<void> {
    const shouldRetry = retryOn.includes(response.status) && attempt < maxAttempts - 1

    // Try to get error message from response
    let errorMessage = `HTTP ${response.status} ${response.statusText}`
    try {
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json()
        errorMessage = errorData.message || errorData.error || errorMessage
      } else {
        const errorText = await response.text()
        errorMessage = errorText || errorMessage
      }
    } catch {
      // Ignore JSON parsing errors
    }

    if (shouldRetry) {
      const delay = retryDelay * Math.pow(2, attempt)
      clientLogger.warn({ errorMessage, delay, attempt: attempt + 1, maxAttempts }, `${errorMessage}. Retrying in ${delay}ms`)
      await this.sleep(delay)
      throw new Error('RETRY') // Signal to retry
    }

    // 401 = expired or invalid token — signal the auth layer to clear the session.
    // Only dispatch when a token was present; guest 401s (no token sent) should not
    // trigger sign-out since there is no session to clear.
    if (response.status === 401 && typeof window !== 'undefined' && getAuthToken()) {
      window.dispatchEvent(new CustomEvent('auth:session-expired'))
    }

    // No retry, throw error
    throw new Error(errorMessage)
  }

  /**
   * Check if error is retryable
   * Can be overridden for custom retry logic
   */
  protected isRetryableError(error: any): boolean {
    const errorMessage = error.message || ''
    return (
      error.name === 'TypeError' || // Network errors
      errorMessage.includes('fetch') ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('RETRY')
    )
  }

  /**
   * Sleep helper for retry delays
   */
  protected sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * Convenience methods for common HTTP verbs
   */
  protected async get<T>(endpoint: string, config?: ApiRequestConfig): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' }, config)
  }

  protected async post<T>(
    endpoint: string,
    body?: any,
    config?: ApiRequestConfig
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      },
      config
    )
  }

  protected async put<T>(
    endpoint: string,
    body?: any,
    config?: ApiRequestConfig
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'PUT',
        body: body ? JSON.stringify(body) : undefined,
      },
      config
    )
  }

  protected async delete<T>(endpoint: string, config?: ApiRequestConfig): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' }, config)
  }

  protected async patch<T>(
    endpoint: string,
    body?: any,
    config?: ApiRequestConfig
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'PATCH',
        body: body ? JSON.stringify(body) : undefined,
      },
      config
    )
  }
}
